import { useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { StepperContext } from 'lib/context/StepperContext/StepperContext';

import { userState } from 'components/state';
import Loader from 'components/shared/Loader/Loader';
import adminState from 'components/admin/lib/adminState';

import IntroStep from '../OnboardingWizard/components/IntroStep/IntroStep';

import useStepperData from './hooks/useStepperData';
import useStepperTour from './hooks/useStepperTour';
import useSidebarState from './hooks/useSidebarState';
import { useWindowWidth } from './hooks/useWindowWidth';
import { useFooterConfig } from './hooks/useFooterConfig';
import { useStepperScroll } from './hooks/useStepperScroll';
import useStepperPageState from './hooks/useStepperPageState';
import { useIntroTransition } from './hooks/useIntroTransition';
import StepperShell from './components/StepperShell/StepperShell';
import useStepperContextValue from './hooks/useStepperContextValue';
import { useStepperOrchestration } from './hooks/useStepperOrchestration';
import StepPlaceholder from './components/StepPlaceholder/StepPlaceholder';
import { INTRO_STEP_DEFAULT_DEFINITION, NARROW_SCREEN_BREAKPOINT } from './lib/constants';

import './lib/Stepper.scss';

const EMPTY_SET = new Set();
const EMPTY_ARRAY = [];

/**
 * @param {import('./lib/propTypes').StepperProps} props
 */
const Stepper = ({
  practiceData = null,
  wizardKind,
  IntroComponent = null,
  tourStepsConfig = [],
  sidebarType = 'default',
  hiddenSubstepCodes = EMPTY_SET,
  restrictedStepsCodes = EMPTY_SET,
  componentStepsConfig = {},
  resolveComponent = null,
  baseUrlSegments,
  wrapWithIntroAndReview = true,
  skipInitialUrlSync = false,
  onStepperExit = null,
  exitConfig = null,
  SmartSuggestionsComponent,
  AssistanceCardComponent,
  LoginComponent,
  BottomSlot,
  wizardStateOverride,
  includeIntro = true,
  includeReview = true,
  syncWithUrl = true,
  requirePracticeForRender = true
} = {}) => {
  const userStateValue = useRecoilValue(userState);
  const adminStateValue = useRecoilValue(adminState.current_admin);
  const currentUserId = userStateValue?.user_id ?? userStateValue?.id;
  const isAuthenticated = userStateValue?.authenticated;
  const isAdmin = !isAuthenticated && adminStateValue?.logged_in;

  const {
    practice,
    mainContentRef,
    showIntro,
    setShowIntro,
    showTasksIntro,
    setShowTasksIntro,
    navigateToCode,
    setNavigateToCode,
    headerButtonProps,
    setHeaderButtonProps,
    headerInfoTooltip,
    setHeaderInfoTooltip
  } = useStepperPageState({
    practiceData,
    isAuthenticated,
    requirePractice: requirePracticeForRender
  });

  const [headerOptionsOverride, setHeaderOptionsOverride] = useState(null);

  const { isNarrowScreen } = useWindowWidth(NARROW_SCREEN_BREAKPOINT);
  const { sidebarOpen, closeSidebar, toggleSidebar } = useSidebarState({ isNarrowScreen });

  const { isLoading, wizardState, wizardInstanceId, consultant, assignedTasks, delegations } =
    useStepperData({
      practice,
      wizardKind,
      currentUserId,
      wizardStateOverride
    });

  const {
    instance,
    phases,
    stepsState,
    activeStep,
    activeSubstep,
    expandStep,
    collapseStep,
    navigateToSubstep,
    goNext,
    goPrev,
    canGoPrev,
    activateStepWithHiddenCheck,
    orchestratorData,
    progressValue
  } = useStepperOrchestration({
    wizardState,
    wizardKind,
    hiddenSubstepCodes,
    navigateToCode,
    componentStepsConfig,
    onNavigateToCodeHandled: () => setNavigateToCode(null),
    baseUrlSegments,
    wrapWithIntroAndReview,
    skipInitialUrlSync,
    syncWithUrl,
    includeIntro,
    includeReview
  });

  const { isEnteringFromIntro, handleIntroNext } = useIntroTransition(goNext);
  const { footerConfig, setFooterConfig, addFooterActions } = useFooterConfig(goPrev);
  const { shouldAnimateHeader } = useStepperScroll(mainContentRef, activeSubstep?.code);
  const { showTour, startTourIfUnseen, handleTourClose } = useStepperTour({
    isAuthenticated,
    wizardInstanceId
  });

  useEffect(() => {
    setHeaderInfoTooltip(null);
  }, [activeSubstep?.code]);

  const componentStepConfig = useMemo(() => {
    const resolved =
      typeof resolveComponent === 'function'
        ? resolveComponent(activeStep, activeSubstep, componentStepsConfig)
        : null;
    return resolved || componentStepsConfig[activeSubstep?.code] || {};
  }, [resolveComponent, activeStep, activeSubstep, componentStepsConfig]);

  const ActiveComponent =
    componentStepConfig.component || componentStepConfig.fallbackComponent || StepPlaceholder;

  const headerOptions = useMemo(
    () => ({
      title: activeSubstep?.name,
      description: activeSubstep?.description,
      ...(componentStepConfig.headerOptions || {}),
      ...(headerOptionsOverride || {})
    }),
    [
      activeSubstep?.name,
      activeSubstep?.description,
      componentStepConfig.headerOptions,
      headerOptionsOverride
    ]
  );

  const resolvedSmartSuggestions = useMemo(
    () => componentStepConfig.smartSuggestions || EMPTY_ARRAY,
    [componentStepConfig]
  );

  const isIntroStep = activeSubstep?.code === INTRO_STEP_DEFAULT_DEFINITION.substeps[0].code;

  const { stepperContextValue, handleActivateStep } = useStepperContextValue({
    isAdmin,
    practice,
    instance,
    stepsState,
    activeSubstep,
    orchestratorData,
    componentStepConfig,
    restrictedStepsCodes,
    goNext,
    goPrev,
    canGoPrev,
    navigateToSubstep,
    activateStepWithHiddenCheck,
    setHeaderButtonProps,
    setHeaderInfoTooltip,
    setHeaderOptionsOverride,
    setFooterConfig,
    addFooterActions
  });

  if (isIntroStep) {
    return (
      <StepperContext.Provider value={stepperContextValue}>
        <div className="flex h-screen w-screen">
          <IntroStep onNext={handleIntroNext} />
        </div>
      </StepperContext.Provider>
    );
  }

  if (isLoading || (requirePracticeForRender && !practice?.id)) return <Loader />;

  return (
    <StepperContext.Provider value={stepperContextValue}>
      <StepperShell
        showTour={showTour}
        tourStepsConfig={tourStepsConfig}
        onTourClose={handleTourClose}
        showIntro={showIntro}
        IntroComponent={IntroComponent}
        consultant={consultant}
        currentUserId={currentUserId}
        isAuthenticated={isAuthenticated}
        setShowIntro={setShowIntro}
        startTourIfUnseen={startTourIfUnseen}
        showTasksIntro={showTasksIntro}
        setShowTasksIntro={setShowTasksIntro}
        assignedTasks={assignedTasks}
        setNavigateToCode={setNavigateToCode}
        isNarrowScreen={isNarrowScreen}
        sidebarOpen={sidebarOpen}
        closeSidebar={closeSidebar}
        toggleSidebar={toggleSidebar}
        isEnteringFromIntro={isEnteringFromIntro}
        mainContentRef={mainContentRef}
        wizardState={wizardState}
        phases={phases}
        sidebarType={sidebarType}
        SmartSuggestionsComponent={SmartSuggestionsComponent}
        AssistanceCardComponent={AssistanceCardComponent}
        LoginComponent={LoginComponent}
        BottomSlot={BottomSlot}
        onExit={onStepperExit}
        exitConfig={exitConfig}
        hiddenSubstepCodes={hiddenSubstepCodes}
        smartSuggestions={resolvedSmartSuggestions}
        onExpandStep={expandStep}
        onCollapseStep={collapseStep}
        onActivateStep={handleActivateStep}
        headerButtonProps={headerButtonProps}
        headerInfoTooltip={headerInfoTooltip}
        headerOptions={headerOptions}
        progressValue={progressValue}
        shouldAnimateHeader={shouldAnimateHeader}
        footerConfig={footerConfig}
        delegations={delegations}
        ActiveComponent={ActiveComponent}
      />
    </StepperContext.Provider>
  );
};

export default Stepper;
